import React, { useEffect, useMemo, useState } from "react";

const API_ROOT = "";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  zIndex: 9999,
};

const modalStyle = {
  width: "min(760px, 100%)",
  maxHeight: "calc(100vh - 40px)",
  overflowY: "auto",
  borderRadius: "24px",
  background: "#fff",
  boxShadow: "0 28px 80px rgba(15, 23, 42, 0.35)",
  border: "1px solid rgba(148, 163, 184, 0.18)",
};

const cardStyle = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "24px",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  background: "#ffffff",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
  cursor: "pointer",
  minHeight: "250px",
  padding: 0,
  textAlign: "left",
};

const filterInputStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "12px 14px",
  background: "#fff",
  color: "#334155",
  fontSize: "14px",
  width: "100%",
};

function courseThumbnailUrl(fileName) {
  return fileName ? `${API_ROOT}/uploads/course-thumbnails/${fileName}` : "https://placehold.co/900x600/e2e8f0/475569?text=Course";
}

function paginateRows(rows, page, pageSize) {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

export function CourseCatalog({
  courses,
  emptyMessage = "No course found.",
  columns = { desktop: 4, tablet: 2, mobile: 1 },
  itemsPerPage = 16,
  enableFilters = false,
}) {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ course_model_id: "", course_category_id: "" });

  const normalizedCourses = useMemo(() => (Array.isArray(courses) ? courses : []), [courses]);

  const modelOptions = useMemo(() => {
    const map = new Map();
    normalizedCourses.forEach((course) => {
      if (course.course_model_id && course.model_name) {
        map.set(String(course.course_model_id), course.model_name);
      }
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [normalizedCourses]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    normalizedCourses.forEach((course) => {
      if (
        course.course_category_id &&
        course.category_name &&
        (!filters.course_model_id || String(course.course_model_id) === String(filters.course_model_id))
      ) {
        map.set(String(course.course_category_id), course.category_name);
      }
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [normalizedCourses, filters.course_model_id]);

  const filteredCourses = useMemo(
    () =>
      normalizedCourses.filter((course) => {
        if (filters.course_model_id && String(course.course_model_id) !== String(filters.course_model_id)) {
          return false;
        }

        if (filters.course_category_id && String(course.course_category_id) !== String(filters.course_category_id)) {
          return false;
        }

        return true;
      }),
    [normalizedCourses, filters]
  );

  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / itemsPerPage));
  const pageRows = useMemo(() => paginateRows(filteredCourses, page, itemsPerPage), [filteredCourses, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [filters.course_model_id, filters.course_category_id]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <>
      {enableFilters ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginBottom: "18px" }}>
          <select
            value={filters.course_model_id}
            onChange={(e) => setFilters({ course_model_id: e.target.value, course_category_id: "" })}
            style={filterInputStyle}
          >
            <option value="">All Modules</option>
            {modelOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <select
            value={filters.course_category_id}
            onChange={(e) => setFilters((prev) => ({ ...prev, course_category_id: e.target.value }))}
            style={filterInputStyle}
          >
            <option value="">All Categories</option>
            {categoryOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
      ) : null}

      {!filteredCourses.length ? (
        <div style={{ color: "#64748b" }}>{emptyMessage}</div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${columns.mobile || 1}, minmax(0, 1fr))`,
              gap: "18px",
            }}
            className="course-catalog-grid"
          >
            {pageRows.map((course) => (
              <button key={course.id} type="button" onClick={() => setSelectedCourse(course)} style={cardStyle}>
                <div style={{ position: "relative", minHeight: "250px" }}>
                  <img
                    src={courseThumbnailUrl(course.thumbnail)}
                    alt={course.title}
                    loading="lazy"
                    decoding="async"
                    style={{ width: "100%", height: "250px", objectFit: "cover", display: "block" }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(180deg, rgba(15,23,42,0.04) 10%, rgba(15,23,42,0.22) 45%, rgba(15,23,42,0.82) 100%)",
                    }}
                  />
                  <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "18px" }}>
                    <h3 style={{ margin: 0, color: "#fff", fontSize: "18px", lineHeight: 1.3 }}>{course.title}</h3>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {totalPages > 1 ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", flexWrap: "wrap", marginTop: "22px" }}>
              <button type="button" disabled={page === 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} className="btn btn-outline-secondary btn-sm">
                Prev
              </button>
              <span style={{ fontSize: "14px", color: "#475569", fontWeight: 700 }}>Page {page} of {totalPages}</span>
              <button type="button" disabled={page === totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} className="btn btn-outline-secondary btn-sm">
                Next
              </button>
            </div>
          ) : null}
        </>
      )}

      {selectedCourse ? (
        <div style={overlayStyle} onClick={() => setSelectedCourse(null)}>
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div style={{ position: "relative" }}>
              <img
                src={courseThumbnailUrl(selectedCourse.thumbnail)}
                alt={selectedCourse.title}
                loading="lazy"
                decoding="async"
                style={{ width: "100%", height: "min(40vw, 300px)", minHeight: "220px", objectFit: "cover", display: "block" }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.3) 52%, rgba(15,23,42,0.82) 100%)",
                }}
              />
              <button
                type="button"
                onClick={() => setSelectedCourse(null)}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  border: "none",
                  width: "42px",
                  height: "42px",
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.9)",
                  color: "#0f172a",
                  fontSize: "18px",
                  fontWeight: 700,
                }}
              >
                x
              </button>
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "24px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}>
                  <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", padding: "8px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: 700 }}>
                    {selectedCourse.semester_count || 1} Semester
                  </span>
                  {selectedCourse.model_name ? (
                    <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", padding: "8px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: 700 }}>
                      {selectedCourse.model_name}
                    </span>
                  ) : null}
                  {selectedCourse.category_name ? (
                    <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", padding: "8px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: 700 }}>
                      {selectedCourse.category_name}
                    </span>
                  ) : null}
                </div>
                <h2 style={{ margin: 0, color: "#fff", fontSize: "28px", lineHeight: 1.25 }}>{selectedCourse.title}</h2>
              </div>
            </div>
            <div style={{ padding: "24px" }}>
              <h4 style={{ margin: "0 0 12px", color: "#0f172a" }}>Course Description</h4>
              <div style={{ color: "#475569", lineHeight: 1.8, fontSize: "15px", whiteSpace: "pre-wrap" }}>
                {selectedCourse.description || "No description added yet."}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        @media (min-width: 768px) {
          .course-catalog-grid {
            grid-template-columns: repeat(${columns.tablet || columns.mobile || 1}, minmax(0, 1fr)) !important;
          }
        }
        @media (min-width: 1200px) {
          .course-catalog-grid {
            grid-template-columns: repeat(${columns.desktop || columns.tablet || columns.mobile || 1}, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </>
  );
}

export function CourseHighlightCard({ course }) {
  if (!course?.title) {
    return null;
  }

  return <CourseCatalog courses={[course]} columns={{ desktop: 1, tablet: 1, mobile: 1 }} itemsPerPage={1} />;
}
